import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Compass } from "lucide-react";
import useHRCopy from "../i18n/useHRCopy";
import LangSwitch from "./LangSwitch";
import BrandSwitch from "../../components/BrandSwitch";
import HRButton from "./HRButton";
import { useBrandTransition } from "../../components/BrandTransitionProvider";

// HR navigation.
// variant="video": fully transparent over the hero (the dark fade belongs to
//   the hero overlay), white links; converts to the solid paper state past 60px.
// variant="solid": permanently in the paper state (pages without a video hero).
// mode="dashboard": no nav links, logout button instead of Giriş.
export default function HRNavBar({ variant = "solid", mode = "site" }) {
  const copy = useHRCopy();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { startTechTransition } = useBrandTransition();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(variant !== "video");
  const close = () => setOpen(false);

  useEffect(() => {
    if (variant !== "video") {
      setScrolled(true);
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [variant]);

  const links = [
    { to: "/anamenu", label: copy.nav.home },
    { to: "/hakkimda", label: copy.nav.about },
    { to: "/hizmetlerim", label: copy.nav.services, also: ["/isverenhizmetlerim", "/adayhizmetlerim"] },
    { to: "/iletisim", label: copy.nav.contact },
  ];
  const isActive = (l) => pathname === l.to || (l.also || []).includes(pathname);

  // Solid (paper) state vs transparent-over-video state. Full literal class
  // strings per branch so the Tailwind purger keeps them.
  const headerCls = scrolled
    ? "fixed top-0 left-0 right-0 z-40 bg-paper/95 backdrop-blur-md border-b border-line transition-colors duration-300"
    : "fixed top-0 left-0 right-0 z-40 bg-transparent border-b border-transparent transition-colors duration-300";

  const linkCls = (active) => {
    if (scrolled) {
      return active
        ? "relative font-hanken font-medium text-[15px] text-ink transition-colors duration-300"
        : "relative font-hanken font-medium text-[15px] text-steel hover:text-ink transition-colors duration-300";
    }
    return active
      ? "relative font-hanken font-medium text-[15px] text-white transition-colors duration-300"
      : "relative font-hanken font-medium text-[15px] text-white/85 hover:text-white transition-colors duration-300";
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    navigate("/giris");
  };

  return (
    <header className={headerCls}>
      {/* Three columns with equal 1fr sides keep the links optically centered
          on the page rather than pushed right by the wide brand switch, and
          unlike absolute centering the columns can never overlap. */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-10 lg:px-16 h-20 flex xl:grid xl:grid-cols-[1fr_auto_1fr] items-center justify-between gap-3 sm:gap-6">
        <div className="min-w-0 justify-self-start">
          <BrandSwitch active="hr" onTechNavigate={startTechTransition} />
        </div>

        <div className="justify-self-center">
          {mode === "site" && (
            <nav className="hidden xl:flex items-center gap-6 2xl:gap-8">
              {links.map((l) => {
                const active = isActive(l);
                return (
                  <Link key={l.to} to={l.to} className={linkCls(active)}>
                    {l.label}
                    {active && (
                      <span
                        aria-hidden="true"
                        className="absolute -bottom-1.5 left-0 right-0 h-[3px] bg-royal"
                      />
                    )}
                  </Link>
                );
              })}
            </nav>
          )}
        </div>

        {/* The right cell mirrors the brand switch's footprint (364px at xl+,
            where the switch is always that wide) and left-aligns its contents,
            so the language/login group sits the same distance from the centered
            links as the switch does on the other side. */}
        <div className="justify-self-end flex items-center justify-end xl:justify-start gap-5 xl:w-[364px]">
          <div className="hidden xl:flex items-center gap-5">
            {mode === "site" && (
              <Link
                to="/"
                className={
                  scrolled
                    ? "inline-flex items-center gap-2 font-hanken font-medium text-[15px] text-steel hover:text-ink transition-colors duration-300"
                    : "inline-flex items-center gap-2 font-hanken font-medium text-[15px] text-white/85 hover:text-white transition-colors duration-300"
                }
              >
                <Compass className="h-[17px] w-[17px]" aria-hidden="true" />
                {copy.nav.navigator}
              </Link>
            )}
            <LangSwitch tone={scrolled ? "dark" : "light"} />
            {mode === "dashboard" ? (
              <HRButton onClick={handleLogout} variant="navy" size="sm">
                {copy.nav.logout}
              </HRButton>
            ) : (
              <HRButton to="/giris" variant={scrolled ? "navy" : "primary"} size="sm">
                {copy.nav.login}
              </HRButton>
            )}
          </div>

          <button
            className={`xl:hidden transition-colors duration-300 ${scrolled ? "text-ink" : "text-white"}`}
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            aria-expanded={open}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>

      {/* Drawer: a ruled list of destinations, the active one carrying the
          same royal bar the desktop links use, then one row of utilities.
          The brand switch is not repeated here — it stays in the bar above. */}
      {open && (
        <div className="xl:hidden bg-paper/95 backdrop-blur-xl border-t border-line max-h-[calc(100vh-5rem)] overflow-y-auto">
          {mode === "site" && (
            <nav aria-label="Mobile" className="px-6 py-2">
              {links.map((l) => {
                const active = isActive(l);
                return (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={close}
                    className={
                      active
                        ? "relative flex items-center py-4 pl-4 border-b border-line font-hanken font-semibold text-[15px] text-ink"
                        : "relative flex items-center py-4 pl-4 border-b border-line font-hanken font-medium text-[15px] text-steel hover:text-ink transition-colors duration-300"
                    }
                  >
                    {active && (
                      <span
                        aria-hidden="true"
                        className="absolute left-0 top-3 bottom-3 w-[3px] bg-royal"
                      />
                    )}
                    {l.label}
                  </Link>
                );
              })}
              <Link
                to="/"
                onClick={close}
                className="flex items-center gap-2 py-4 pl-4 font-hanken font-medium text-[15px] text-steel hover:text-ink transition-colors duration-300"
              >
                <Compass className="h-[17px] w-[17px]" aria-hidden="true" />
                {copy.nav.navigator}
              </Link>
            </nav>
          )}
          <div className="px-6 py-4 border-t border-line flex items-center justify-between gap-4">
            <LangSwitch tone="dark" />
            {mode === "dashboard" ? (
              <HRButton onClick={() => { handleLogout(); close(); }} variant="navy" size="sm">
                {copy.nav.logout}
              </HRButton>
            ) : (
              <HRButton to="/giris" onClick={close} variant="navy" size="sm">
                {copy.nav.login}
              </HRButton>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
